// src/features/screener/components/ScreenerFilters.tsx
//
// The institutional-terminal filter rail for the Global Screener. Owns
// its own local input state (as strings, so typing stays smooth) and
// emits a NORMALISED `ScreenerFilters` object upward whenever anything
// changes. The parent page debounces that object before hitting the API,
// which is what makes the screener feel live without hammering the
// backend on every keystroke.
//
// Unit conveniences (converted to raw units before emitting):
//   * Market Cap is entered in $B (billions) — 50 -> 50_000_000_000.
//   * Volume is entered in millions of shares — 1 -> 1_000_000.
// Everything else (price, beta, dividend) is entered in raw units.

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import type { ScreenerFilters, ScreenerOptions } from '../api';

interface ScreenerFiltersPanelProps {
    options: ScreenerOptions;
    onChange: (filters: ScreenerFilters) => void;
    onActiveCountChange?: (count: number) => void;
}

// Tri-state ETF control. `null` == no filter (all asset classes).
type EtfMode = 'all' | 'equities' | 'etfs';

// Parse a user string into a positive number, or undefined when blank /
// invalid. Undefined fields are dropped from the payload upstream.
const parseNum = (raw: string): number | undefined => {
    const trimmed = raw.trim();
    if (trimmed === '') return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : undefined;
};

const B = 1_000_000_000;
const M = 1_000_000;

export const ScreenerFiltersPanel = ({
    options,
    onChange,
    onActiveCountChange,
}: ScreenerFiltersPanelProps) => {
    // --- Local string state (source of truth for the inputs). ------------
    const [marketCapMin, setMarketCapMin] = useState('');
    const [marketCapMax, setMarketCapMax] = useState('');
    const [priceMin, setPriceMin] = useState('');
    const [priceMax, setPriceMax] = useState('');
    const [betaMin, setBetaMin] = useState('');
    const [dividendMin, setDividendMin] = useState('');
    const [volumeMin, setVolumeMin] = useState('');
    const [sector, setSector] = useState('');
    // Default the screener to NYSE equities: market caps are stored in each
    // listing's NATIVE currency, so an "Any exchange" market-cap sort ranks
    // e.g. KRW-denominated Korean names above USD ones. Opening on NYSE gives
    // a like-for-like (USD) "largest companies" view out of the box.
    const [exchange, setExchange] = useState('NYSE');
    const [etfMode, setEtfMode] = useState<EtfMode>('equities');

    // --- Normalise local state -> canonical ScreenerFilters. -------------
    const filters = useMemo<ScreenerFilters>(() => {
        const next: ScreenerFilters = {};

        const mcMin = parseNum(marketCapMin);
        if (mcMin !== undefined) next.market_cap_more_than = Math.round(mcMin * B);
        const mcMax = parseNum(marketCapMax);
        if (mcMax !== undefined) next.market_cap_lower_than = Math.round(mcMax * B);

        const pMin = parseNum(priceMin);
        if (pMin !== undefined) next.price_more_than = pMin;
        const pMax = parseNum(priceMax);
        if (pMax !== undefined) next.price_lower_than = pMax;

        const bMin = parseNum(betaMin);
        if (bMin !== undefined) next.beta_more_than = bMin;

        const dMin = parseNum(dividendMin);
        if (dMin !== undefined) next.dividend_more_than = dMin;

        const vMin = parseNum(volumeMin);
        if (vMin !== undefined) next.volume_more_than = Math.round(vMin * M);

        if (sector) next.sector = sector;
        if (exchange) next.exchange = exchange;

        if (etfMode === 'etfs') next.is_etf = true;
        else if (etfMode === 'equities') next.is_etf = false;

        return next;
    }, [
        marketCapMin,
        marketCapMax,
        priceMin,
        priceMax,
        betaMin,
        dividendMin,
        volumeMin,
        sector,
        exchange,
        etfMode,
    ]);

    // Emit upward whenever the normalised filters change.
    useEffect(() => {
        onChange(filters);
        onActiveCountChange?.(Object.keys(filters).length);
        // onChange / onActiveCountChange are stable enough for this pattern;
        // re-emitting only when the filter payload itself changes is exactly
        // what we want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleReset = () => {
        setMarketCapMin('');
        setMarketCapMax('');
        setPriceMin('');
        setPriceMax('');
        setBetaMin('');
        setDividendMin('');
        setVolumeMin('');
        setSector('');
        setExchange('');
        setEtfMode('all');
    };

    const activeCount = Object.keys(filters).length;
    // Beta + dividend are meaningless for digital assets, so grey them out
    // when the user scopes the screener to the CRYPTO "exchange".
    const isCrypto = exchange === 'CRYPTO';

    return (
        <div className="surface-card p-5 sm:p-6">
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                    <h2 className="section-title">Filters</h2>
                    {activeCount > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                            {activeCount}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleReset}
                    disabled={activeCount === 0}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    Reset all
                </button>
            </div>

            {/* Asset-type segmented control. */}
            <FieldLabel>Asset Type</FieldLabel>
            <div className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-900/60 mb-5">
                {(
                    [
                        ['all', 'All'],
                        ['equities', 'Ex-ETF'],
                        ['etfs', 'ETFs'],
                    ] as [EtfMode, string][]
                ).map(([mode, label]) => (
                    <button
                        key={mode}
                        type="button"
                        onClick={() => setEtfMode(mode)}
                        className={`py-2 rounded-lg text-sm font-semibold transition-all ${
                            etfMode === mode
                                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Sector + Exchange dropdowns. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <SelectField
                    label="Sector"
                    value={sector}
                    onChange={setSector}
                    placeholder="Any sector"
                    options={options.sectors}
                />
                <SelectField
                    label="Exchange"
                    value={exchange}
                    onChange={setExchange}
                    placeholder="Any exchange"
                    options={options.exchanges}
                />
            </div>

            {/* Market-cap range ($B). */}
            <FieldLabel>Market Cap ($B)</FieldLabel>
            <div className="grid grid-cols-2 gap-3 mb-5">
                <NumberField value={marketCapMin} onChange={setMarketCapMin} placeholder="Min" />
                <NumberField value={marketCapMax} onChange={setMarketCapMax} placeholder="Max" />
            </div>

            {/* Price range ($). */}
            <FieldLabel>Price ($)</FieldLabel>
            <div className="grid grid-cols-2 gap-3 mb-5">
                <NumberField value={priceMin} onChange={setPriceMin} placeholder="Min" />
                <NumberField value={priceMax} onChange={setPriceMax} placeholder="Max" />
            </div>

            {/* Single-sided minimums. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                    <FieldLabel>Min Beta</FieldLabel>
                    <NumberField
                        value={betaMin}
                        onChange={setBetaMin}
                        placeholder={isCrypto ? 'N/A' : 'e.g. 1.0'}
                        step="0.1"
                        disabled={isCrypto}
                    />
                </div>
                <div>
                    <FieldLabel>Min Dividend ($)</FieldLabel>
                    <NumberField
                        value={dividendMin}
                        onChange={setDividendMin}
                        placeholder={isCrypto ? 'N/A' : 'e.g. 1.5'}
                        step="0.1"
                        disabled={isCrypto}
                    />
                </div>
                <div>
                    <FieldLabel>Min Volume (M)</FieldLabel>
                    <NumberField value={volumeMin} onChange={setVolumeMin} placeholder="e.g. 1" />
                </div>
            </div>
        </div>
    );
};

// --- Small presentational primitives (kept local to the feature). -------

const FieldLabel = ({ children }: { children: ReactNode }) => (
    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
        {children}
    </label>
);

interface NumberFieldProps {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    step?: string;
    disabled?: boolean;
}

const NumberField = ({ value, onChange, placeholder, step, disabled }: NumberFieldProps) => (
    <input
        type="number"
        inputMode="decimal"
        min={0}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full rounded-lg border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:border-emerald-400 transition-shadow [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            disabled
                ? 'bg-slate-100 dark:bg-slate-900/60 opacity-60 cursor-not-allowed'
                : 'bg-white dark:bg-slate-800'
        }`}
    />
);

interface SelectFieldProps {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    options: string[];
}

const SelectField = ({ label, value, onChange, placeholder, options }: SelectFieldProps) => (
    <div>
        <FieldLabel>{label}</FieldLabel>
        <div className="relative">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full appearance-none rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 pr-9 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:border-emerald-400 transition-shadow cursor-pointer"
            >
                <option value="">{placeholder}</option>
                {options.map((opt) => (
                    <option key={opt} value={opt}>
                        {opt}
                    </option>
                ))}
            </select>
            <svg
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6" />
            </svg>
        </div>
    </div>
);

export default ScreenerFiltersPanel;
